import React, { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, Plus, Save, Trash2 } from 'lucide-react'
import styles from './AiSkillsTab.module.css'
import InputModal from '../../../ui/inputModal/InputModal'
import { aiSkillsApi } from '../../../lib/apiClient'
import { toast } from '../../../lib/toast'
import type { AiSkill, CreateAiSkillDto, UpdateAiSkillDto } from '../../../types/aiSkills'
import { emptyCreateAiSkillForm } from '../../../types/aiSkills'
import { typeMeta, getTypeMeta } from '../../../utils/utils'

const AiSkillsTab: React.FC = () => {
  const [skills, setSkills] = useState<AiSkill[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [drafts, setDrafts] = useState<Record<string, Partial<{ label: string; description: string; content: string }>>>({})
  const [saving, setSaving] = useState<Record<string, boolean>>({})
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createForm, setCreateForm] = useState<CreateAiSkillDto>(emptyCreateAiSkillForm)
  const [creating, setCreating] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<{ key: string; label: string } | null>(null)

  useEffect(() => { load() }, [])

  const load = async () => {
    try {
      const data = await aiSkillsApi.list()
      setSkills(data)
    } catch {
      toast.error('Failed to load AI skills')
    } finally {
      setLoading(false)
    }
  }

  const toggleExpand = (key: string) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const getDraft = (skill: AiSkill) => {
    const d = drafts[skill.key]
    return {
      label: d?.label ?? skill.label,
      description: d?.description ?? skill.description,
      content: d?.content ?? skill.content,
    }
  }

  const setDraftField = (key: string, field: string, value: string) => {
    setDrafts(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }))
  }

  const handleSave = async (skill: AiSkill) => {
    const d = getDraft(skill)
    const dto: UpdateAiSkillDto = { label: d.label, description: d.description, content: d.content }
    setSaving(prev => ({ ...prev, [skill.key]: true }))
    try {
      const updated = await aiSkillsApi.update(skill.key, dto)
      setSkills(prev => prev.map(s => s.key === skill.key ? updated : s))
      setDrafts(prev => { const n = { ...prev }; delete n[skill.key]; return n })
      toast.success('Skill saved')
    } catch {
      toast.error('Failed to save skill')
    } finally {
      setSaving(prev => ({ ...prev, [skill.key]: false }))
    }
  }

  const handleToggle = async (skill: AiSkill) => {
    try {
      const updated = await aiSkillsApi.update(skill.key, { enabled: !skill.enabled })
      setSkills(prev => prev.map(s => s.key === skill.key ? updated : s))
    } catch {
      toast.error('Failed to update skill')
    }
  }

  const handleDelete = async (key: string) => {
    try {
      await aiSkillsApi.delete(key)
      setSkills(prev => prev.filter(s => s.key !== key))
      toast.success('Skill deleted')
    } catch {
      toast.error('Failed to delete skill')
    }
    setPendingDelete(null)
  }

  const handleCreate = async () => {
    if (!createForm.key.trim() || !createForm.label.trim() || !createForm.content.trim()) {
      toast.error('Key, label, and content are required')
      return
    }
    setCreating(true)
    try {
      const created = await aiSkillsApi.create(createForm)
      setSkills(prev => [created, ...prev])
      setCreateForm(emptyCreateAiSkillForm)
      setShowCreateForm(false)
      toast.success('Skill created')
    } catch {
      toast.error('Failed to create skill')
    } finally {
      setCreating(false)
    }
  }

  if (loading) return <div className={styles.loading}>Loading skills…</div>

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>AI Skills Library</h2>
          <p className={styles.subtitle}>
            Reusable knowledge snippets injected into the AI agent before tool-building sessions.
          </p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowCreateForm(v => !v)}>
          <Plus size={15} />
          {showCreateForm ? 'Cancel' : 'Add Skill'}
        </button>
      </div>

      {showCreateForm && (
        <div className={styles.createCard}>
          <p className={styles.createTitle}>New Skill</p>
          <div className={styles.formRow}>
            <label className={styles.label}>
              Key <span className={styles.required}>*</span>
              <input
                className={styles.textInput}
                placeholder="e.g. sap_odata_auth"
                value={createForm.key}
                onChange={e => setCreateForm(f => ({ ...f, key: e.target.value }))}
              />
            </label>
            <label className={styles.label}>
              Label <span className={styles.required}>*</span>
              <input
                className={styles.textInput}
                placeholder="Human-readable name"
                value={createForm.label}
                onChange={e => setCreateForm(f => ({ ...f, label: e.target.value }))}
              />
            </label>
          </div>
          <div className={styles.formRow}>
            <label className={styles.label}>
              Description
              <input
                className={styles.textInput}
                placeholder="Short description"
                value={createForm.description}
                onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
              />
            </label>
            <label className={styles.label}>
              Connector Type
              <select
                className={styles.textInput}
                value={createForm.connectorTypeId ?? ''}
                onChange={e => setCreateForm(f => ({ ...f, connectorTypeId: e.target.value ? Number(e.target.value) : undefined }))}
              >
                <option value="">General (all connectors)</option>
                {typeMeta.map(t => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </select>
            </label>
          </div>
          <label className={styles.label}>
            Content <span className={styles.required}>*</span>
            <textarea
              className={styles.contentTextarea}
              rows={6}
              placeholder="Skill content - instructions the AI will read before building tools"
              value={createForm.content}
              onChange={e => setCreateForm(f => ({ ...f, content: e.target.value }))}
            />
          </label>
          <div className={styles.createActions}>
            <button className={styles.saveBtn} onClick={handleCreate} disabled={creating}>
              <Save size={14} />
              {creating ? 'Creating…' : 'Create Skill'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.list}>
        {skills.length === 0 && (
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>No skills yet. Create one to get started.</p>
        )}
        {skills.map(skill => {
          const isExpanded = !!expanded[skill.key]
          const draft = getDraft(skill)
          const isDirty =
            draft.label !== skill.label ||
            draft.description !== skill.description ||
            draft.content !== skill.content
          const connectorName = skill.connectorTypeId
            ? (getTypeMeta(skill.connectorTypeId)?.title ?? `Type ${skill.connectorTypeId}`)
            : null

          return (
            <div key={skill.key} className={styles.card}>
              <div className={styles.cardHeader} onClick={() => toggleExpand(skill.key)}>
                <div className={styles.cardHeaderLeft}>
                  <span className={styles.cardLabel}>{skill.label}</span>
                  <span className={`${styles.badge} ${skill.source === 'auto' ? styles.badgeAuto : styles.badgeManual}`}>
                    {skill.source}
                  </span>
                  <span className={`${styles.badge} ${connectorName ? styles.badgeConnector : styles.badgeGeneral}`}>
                    {connectorName ?? 'General'}
                  </span>
                  <label className={styles.toggle} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className={styles.toggleInput}
                      checked={skill.enabled}
                      onChange={() => handleToggle(skill)}
                    />
                    <span className={`${styles.toggleTrack} ${skill.enabled ? styles.toggleTrackOn : ''}`}>
                      <span className={`${styles.toggleThumb} ${skill.enabled ? styles.toggleThumbOn : ''}`} />
                    </span>
                  </label>
                </div>
                <button className={styles.collapseBtn} tabIndex={-1}>
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>

              {isExpanded && (
                <div className={styles.cardBody}>
                  <div className={styles.formRow}>
                    <label className={styles.label}>
                      Label
                      <input
                        className={styles.textInput}
                        value={draft.label}
                        onChange={e => setDraftField(skill.key, 'label', e.target.value)}
                      />
                    </label>
                    <label className={styles.label}>
                      Description
                      <input
                        className={styles.textInput}
                        value={draft.description}
                        onChange={e => setDraftField(skill.key, 'description', e.target.value)}
                      />
                    </label>
                  </div>
                  <label className={styles.label}>
                    Content
                    <textarea
                      className={styles.contentTextarea}
                      rows={8}
                      value={draft.content}
                      onChange={e => setDraftField(skill.key, 'content', e.target.value)}
                    />
                  </label>
                  <div className={styles.cardActions}>
                    <button
                      className={styles.saveBtn}
                      onClick={() => handleSave(skill)}
                      disabled={!isDirty || saving[skill.key]}
                    >
                      <Save size={14} />
                      {saving[skill.key] ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => setPendingDelete({ key: skill.key, label: skill.label })}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <InputModal
        open={!!pendingDelete}
        title="Delete Skill"
        message={`Delete "${pendingDelete?.label}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        showInput={false}
        danger
        onConfirm={() => pendingDelete && handleDelete(pendingDelete.key)}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}

export default AiSkillsTab
